/* <m-option-drawer> — one drawer per product option.
 *
 * Two constraints pull in opposite directions, and everything below exists to
 * satisfy both.
 *
 * 1. The markup must be RENDERED inside <variant-picker>. product-info-bento.js
 *    re-fetches the section on every option change and replaces that whole
 *    element (ProductInfo.replacePicker), so only markup living inside it gets
 *    fresh prices and availability.
 *
 * 2. The drawer must be DISPLAYED at <body> level. Anywhere inside the product
 *    section it paints under the header, and no z-index fixes it — verified on
 *    the live theme: raising the nearest stacking context to 99999 changed
 *    nothing, moving the node to <body> fixed it instantly. Neutralising the
 *    sticky ancestor and the header will-change changed nothing either. This is
 *    why every other overlay in this theme (cart drawer, search popup, quick
 *    view) is rendered at body level in layout/theme.liquid.
 *
 * So the node is portalled to <body> while open, and sent home before it
 * matters. "Before it matters" is precise: product-info-bento.js takes only radios
 * inside <variant-picker> (its change listener sits on <product-info>) and
 * reads the selection from that element's DESCENDANTS. A radio that changes
 * while the drawer sits at body level is invisible to it and the wrong variant
 * resolves. The change handler therefore moves the node
 * home FIRST, then re-dispatches the event from the radio, now back inside.
 */
if (!customElements.get("m-option-drawer")) {
  const openDrawers = new Set();
  const portalled = new Map(); // drawer key -> element currently at body level
  const SCROLL_LOCK = "prevent-scroll";

  /* The scroll lock is shared with the cart drawer and the bento drawers
     (search among them). Only release it when nothing of ours is open AND no other
     overlay is holding it, otherwise closing an option drawer would unlock the
     page behind an open cart drawer. */
  function releaseScrollLock() {
    if (openDrawers.size > 0) return;
    if (document.querySelector(".m-cart-drawer--active, .m-bento-drawer.is-open")) return;
    document.documentElement.classList.remove(SCROLL_LOCK);
  }

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class MOptionDrawer extends HTMLElement {
    connectedCallback() {
      if (!this.ready) {
        this.init();
        this.ready = true;
      }

      // Restore state after product-info-bento.js swapped the DOM. No rAF: the drawer
      // must be open in the same frame it is inserted, so the visitor never
      // sees it blink shut.
      if (openDrawers.has(this.key) && !this.classList.contains("is-open")) {
        this.portal();
        this.wrapper.classList.remove("m:hidden");
        this.classList.add("is-open");
        document.documentElement.classList.add(SCROLL_LOCK);
        document.addEventListener("keydown", this.onKeydown);
        this.trigger?.setAttribute("aria-expanded", "true");
      }
    }

    init() {
      this.wrapper = this.querySelector(".m-drawer__wrapper");
      this.content = this.querySelector(".m-drawer__content");
      this.key = this.dataset.drawerKey;
      this.onKeydown = this.onKeydown.bind(this);

      this.trigger = this.dataset.triggerId
        ? document.getElementById(this.dataset.triggerId)
        : null;
      this.trigger?.addEventListener("click", () => this.open());

      this.querySelector("[data-option-drawer-close]")?.addEventListener(
        "click",
        () => this.close()
      );

      this.wrapper?.addEventListener("click", (event) => {
        if (event.target === this.wrapper) this.close();
      });

      this.addEventListener("change", (event) => {
        if (this.forwarding) return;
        const input = event.target;

        // The picker only sees radios that are its own descendants, so the node
        // has to be home before this event reaches it. The original event is
        // stopped and replayed from the same input once it is back inside.
        event.stopPropagation();
        // The real node has to go home now, and product-info-bento.js replaces
        // it a moment later, so it cannot play the close itself: a copy at
        // body level slides out in its place (like the cart drawer) while
        // the real one is hidden at once.
        const ghost = this.slideOutCopy();
        this.sendHome();
        this.close(!!ghost);
        this.resetGroup(input);

        this.forwarding = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
        this.forwarding = false;
      });
    }

    /* Frame drawer (snippets/product-frame-options.liquid): choosing Unframed
       resets the passe-partout option to its first value (None) — Unframed
       has no variant with a passe-partout, so the old selection would resolve
       to nothing. Runs after sendHome(), while the event is still stopped, so
       product-info-bento.js reads both radios when the replayed event arrives. */
    resetGroup(input) {
      const group = input.dataset.resetGroup;
      if (!group) return;
      const picker = this.closest("variant-picker");
      if (!picker) return;
      // Trimmed: product-variant-options renders the name with a trailing
      // newline ("Passe-partout-3\n"), so a strict match finds nothing.
      const first = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
        (radio) => radio.name.trim() === group
      );
      if (first) first.checked = true;
    }

    disconnectedCallback() {
      // Deliberately does NOT touch openDrawers: the picker swap removes the
      // old node while the new one takes over, so clearing state here would
      // close the drawer that was just restored. A portal move also fires this;
      // `moving` marks that case.
      if (this.moving) return;
      document.removeEventListener("keydown", this.onKeydown);
    }

    /* Move to <body> so the overlay escapes the product section paint order. */
    portal() {
      if (this.parentElement === document.body) return;

      // A previous element for this key can be stranded at body level if its
      // picker was replaced while the drawer was open. Drop it.
      const stale = portalled.get(this.key);
      if (stale && stale !== this && stale.parentElement === document.body) {
        stale.moving = true;
        stale.remove();
      }

      this.home = this.parentElement;
      this.moving = true;
      document.body.appendChild(this);
      this.moving = false;
      portalled.set(this.key, this);
    }

    sendHome() {
      portalled.delete(this.key);
      if (this.parentElement !== document.body) return;
      if (!this.home?.isConnected) return;
      this.moving = true;
      this.home.appendChild(this);
      this.moving = false;
    }

    open() {
      if (this.classList.contains("is-open")) return;

      // portal() first, openDrawers.add() after: appendChild fires
      // connectedCallback synchronously, and that callback restores is-open
      // immediately (no rAF) whenever the key is already in openDrawers, for
      // the mid-interaction case where product-info-bento.js just swapped the
      // picker back in. Adding the key before portal() made a first, genuine
      // open take that same instant path — is-open landed in the same frame
      // as removing m:hidden, so the browser had nothing to animate from and
      // the drawer snapped open instead of sliding in.
      this.portal();
      openDrawers.add(this.key);
      this.wrapper.classList.remove("m:hidden");
      document.documentElement.classList.add(SCROLL_LOCK);
      this.trigger?.setAttribute("aria-expanded", "true");

      // One frame between "displayed" and "open" so the transform has a
      // starting value to animate from.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add("is-open"));
      });

      document.addEventListener("keydown", this.onKeydown);
      this.content?.focus();
    }

    /* A static copy of the open drawer, appended to <body> and sent closed on
       the next frame, so the close transition plays even though the real node
       is about to be replaced. Plain <div> (no upgrade, no listeners), inert,
       without ids and without radio names or form owners, so it neither
       duplicates ids nor joins the product form's radio groups. Returns null
       when the drawer is not open at body level. */
    slideOutCopy() {
      if (this.parentElement !== document.body || !this.classList.contains("is-open")) return null;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return null;
      const ghost = document.createElement("div");
      ghost.className = this.className;
      ghost.setAttribute("aria-hidden", "true");
      ghost.inert = true;
      ghost.append(this.wrapper.cloneNode(true));
      ghost.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
      ghost.querySelectorAll("input").forEach((el) => {
        el.removeAttribute("name");
        el.removeAttribute("form");
      });
      document.body.appendChild(ghost);
      // Commit the open styles first (forced style flush), then close in the
      // same task: the transition starts from the committed open state.
      void ghost.offsetWidth;
      ghost.classList.remove("is-open");
      setTimeout(() => ghost.remove(), 450);
      return ghost;
    }

    close(instant = false) {
      if (!this.classList.contains("is-open")) return;

      openDrawers.delete(this.key);
      this.classList.remove("is-open");
      this.trigger?.setAttribute("aria-expanded", "false");
      document.removeEventListener("keydown", this.onKeydown);

      const wrapper = this.wrapper;
      if (instant) {
        // A slide-out copy plays the transition (slideOutCopy).
        wrapper?.classList.add("m:hidden");
        this.sendHome();
        releaseScrollLock();
        this.trigger?.focus({ preventScroll: true });
        return;
      }
      setTimeout(() => {
        // The node may already be gone: product-info-bento.js replaces it while the
        // close transition runs.
        if (!wrapper?.isConnected) return;
        if (this.classList.contains("is-open")) return;
        wrapper.classList.add("m:hidden");
        this.sendHome();
      }, 300);

      releaseScrollLock();
      this.trigger?.focus();
    }

    onKeydown(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        this.close();
        return;
      }

      if (event.key !== "Tab" || !this.content) return;

      const focusable = Array.from(this.content.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === this.content
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define("m-option-drawer", MOptionDrawer);
}
